import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { message } from "antd";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import "./AdminGallery.css";

const AdminGallery = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const normalizeImgUrl = (imgValue) => {
    // backend now stores like "/gallery/xxx.png"
    const v = String(imgValue || "").trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    if (v.startsWith("/")) return v;
    // if old DB stored only filename
    return `/gallery/${v.replace(/^gallery[\\/]/, "")}`;
  };

  function handleFileChange(e) {
    setFile(e.target.files?.[0] || null);
  }

  async function getAllImages() {
    try {
      setLoading(true);
      const res = await axios.get("/api/image/get-images", {
        headers: getAuthHeaders(),
      });

      if (res.data?.success) {
        setImages(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        message.error(res.data?.message || "Failed to fetch images");
        setImages([]);
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to fetch images");
      setImages([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!file) {
      message.error("Please select an image first");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("name", name);

      const res = await axios.post("/api/image/upload", formData, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (res.data?.success) {
        message.success(res.data.message || "Uploaded");
        setName("");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        getAllImages();
      } else {
        message.error(res.data?.message || "Upload failed");
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong");
    }
  }

  async function handleDelete(id) {
    const confirmDelete = window.confirm("Are you sure to delete?");
    if (!confirmDelete) return;

    try {
      const res = await axios.post(
        "/api/image/delete",
        { id },
        { headers: getAuthHeaders() }
      );

      if (res.data?.success) {
        message.success(res.data.message || "Deleted");

        // instant UI update
        setImages((prev) => prev.filter((img) => img._id !== id));
      } else {
        message.error(res.data?.message || "Delete failed");
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong");
    }
  }

  useEffect(() => {
    getAllImages();
  }, []);

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title">
          <h3 className="m-0">Gallery</h3>
        </div>
        <hr />

        <div className="gallery-container">
          {/* Upload Form */}
          <form className="gallery-form" onSubmit={handleSubmit}>
            <input
              className="form-control"
              aria-label="Image Name"
              type="text"
              placeholder="Enter Image Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <input
              className="form-control"
              aria-label="Select Image"
              type="file"
              accept=".jpg, .jpeg, .png, .webp"
              name="image"
              required
              ref={fileInputRef}
              onChange={handleFileChange}
            />

            <button type="submit" className="gallery-btn">
              Upload
            </button>
          </form>

          {/* List */}
          <div className="gallery-toolbar">
            <div className="gallery-count">
              {loading ? "Loading..." : `Total: ${images.length}`}
            </div>
            <button
              type="button"
              className="gallery-btn outline"
              onClick={getAllImages}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          <div className="table-wrap">
            <table className="table table-bordered admin-gallery-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sr No</th>
                  <th style={{ width: 180 }}>Name</th>
                  <th style={{ width: 120 }}>Image</th>
                  <th>Image Path</th>
                  <th style={{ width: 90 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {!loading && images.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty-row">
                      No images found
                    </td>
                  </tr>
                ) : (
                  images.map((item, index) => {
                    const url = normalizeImgUrl(item?.image);

                    return (
                      <tr key={item._id}>
                        <td>{index + 1}</td>
                        <td className="name-cell">{item?.name || "-"}</td>

                        <td>
                          {url ? (
                            <img
                              className="gallery-thumb"
                              src={url}
                              alt={item?.name || "img"}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>

                        <td className="path-cell">{url || "-"}</td>

                        <td>
                          <DeleteIcon
                            className="del-icon"
                            onClick={() => handleDelete(item._id)}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card view */}
          <div className="gallery-cards">
            {images.map((item) => {
              const url = normalizeImgUrl(item?.image);
              return (
                <div className="gallery-card" key={item._id}>
                  <div className="card-left">
                    {url ? (
                      <img
                        className="gallery-card-img"
                        src={url}
                        alt={item?.name || "img"}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="gallery-card-img placeholder">No Image</div>
                    )}
                  </div>

                  <div className="card-mid">
                    <div className="card-title">{item?.name || "-"}</div>
                    <div className="card-sub">{url || "-"}</div>
                  </div>

                  <div className="card-right">
                    <DeleteIcon
                      className="del-icon"
                      onClick={() => handleDelete(item._id)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
